import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useAvailableColors from "@client.hooks/useAvailableColors";
import {
  getTopTimeConsumingMeetingsAsync,
  makeTopTimeConsumingMeetings,
} from "../reducer";
import ChartWithDetailedTooltip from "../ChartWithDetailedTooltip";
import FormatHelper from "@client.utils/FormatHelper";
import { makeProfile } from "@client.pages/Login/reducer";
import "./TopTimeConsumingMeetings.scss";
import Tooltip from "@client.components/Tooltip";
import { filterPropTypes } from "../utils";
import { showDetails } from "../consts";
import useModal from "@client.hooks/useModal";
import { useNavigate } from "react-router-dom";

const CustomYAxisTick = ({ y, payload }) => {
  return (
    <g transform={`translate(${0},${y})`}>
      <foreignObject x={10} y={-10} width={120} height={18}>
        <Tooltip
          content={
            <span className="recharts-text-tooltip">{payload.value}</span>
          }
          style={{ width: 120 }}
        >
          <div className="recharts-text">{payload.value}</div>
        </Tooltip>
      </foreignObject>
    </g>
  );
};

CustomYAxisTick.propTypes = {
  y: PropTypes.number.isRequired,
  payload: PropTypes.any.isRequired,
};

const TopTimeConsumingMeetings = ({ filter, onError }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const topTimeConsumingMeetings = useSelector(makeTopTimeConsumingMeetings);
  const profile = useSelector(makeProfile);
  const { showModal } = useModal();

  const dispatch = useDispatch();

  if (topTimeConsumingMeetings.error) {
    onError(topTimeConsumingMeetings.error);
  }

  useEffect(() => {
    dispatch(
      getTopTimeConsumingMeetingsAsync({
        ...filter,
        isCostIndexPresentation: profile.isCostIndexPresentation,
      })
    );
  }, [dispatch, filter, profile]);

  const [getSubsequentColor] = useAvailableColors();

  const formatXAxis = (value) =>
    t("components.TopTimeConsumingMeetings.xAxisFormat", { value });

  const getDetailedInformation = useCallback(
    (payload) => {
      const tooltip = FormatHelper.getCountableTermValue(
        t,
        "components.TopTimeConsumingMeetings.title",
        payload.meetings.length,
        {
          meetingName: payload.name,
          meetingQty: payload.meetings.length,
        }
      );

      const sortedParticipants = [...payload.participants].sort(
        (a, b) => a.isExternal - b.isExternal
      );

      const participants = sortedParticipants
        .map((p) => `${p.name} (${p.value})`)
        .join(", ");

      const participantsCaption = t(
        "components.TopTimeConsumingMeetings.details",
        {
          meetingQty: payload.meetings.length,
          participants: participants,
        }
      );

      return { tooltip: tooltip, details: participantsCaption };
    },
    [t]
  );

  const showDetailsModal = useCallback(
    (payload) => {
      showDetails(
        payload,
        showModal,
        getDetailedInformation,
        t,
        filter,
        navigate
      );
    },
    [showModal, getDetailedInformation, t, filter, navigate]
  );

  return (
    <ChartWithDetailedTooltip
      title={t("components.Charts.topTimeConsumingMeetings")}
      className="top-time-consuming"
      dataSource={topTimeConsumingMeetings}
      onError={onError}
      isHorizontal={true}
      renderChartContent={(
        handleBarMouseEnter,
        handleBarMouseMove,
        handleBarMouseLeave
      ) => {
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={topTimeConsumingMeetings.data}
              margin={{
                top: 20,
                right: 70,
                left: 50,
                bottom: 5,
              }}
              barSize={20}
            >
              <CartesianGrid className="cartesian-grid" />
              <XAxis
                type="number"
                tickFormatter={formatXAxis}
                axisLine={false}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={100}
                tick={CustomYAxisTick}
                axisLine={false}
              />
              <Bar
                dataKey="value"
                radius={[9, 9, 9, 9]}
                onMouseMove={handleBarMouseMove}
                onMouseEnter={handleBarMouseEnter}
                onMouseLeave={handleBarMouseLeave}
                onClick={showDetailsModal}
                className="clickable-item"
              >
                {topTimeConsumingMeetings.data.map((entry, index) => (
                  <Cell
                    key={`ttcm-${index}`}
                    fill={getSubsequentColor(index)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      }}
    />
  );
};

TopTimeConsumingMeetings.propTypes = {
  onError: PropTypes.func.isRequired,
  filter: filterPropTypes,
};

export default TopTimeConsumingMeetings;
